import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CaretLeft, ChartBar } from '@phosphor-icons/react';
import { useAuth } from '../../../App';
import { SkeletonBlock, EmptyState, CategoryPie } from '../../../components/module-ui';
import { getEconomicsSpendMixDetail } from '../api';
import { money, StatCard, Panel, EmptyHint, SuperAdminGate, EconomicsSourceNote, LineMoney } from './economicsShared';
import { fmtLabel } from '../ui';

const CATEGORIES = [
  { id: 'all', label: 'All categories', api: null },
  { id: 'people', label: 'People', api: 'people' },
  { id: 'infra', label: 'Infrastructure', api: 'infra' },
  { id: 'devices', label: 'Devices', api: 'devices' },
];

function EconomicsSpendMixPage() {
  const { category: categoryParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const activeCat = (categoryParam || 'all').toLowerCase();
  const apiCat = CATEGORIES.find((c) => c.id === activeCat)?.api ?? null;
  const selectedProduct = searchParams.get('product') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Always load ALL products so the product switcher is complete; filter client-side.
    getEconomicsSpendMixDetail(apiCat, null)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user?.role, apiCat]);

  const currency = data?.currency || 'INR';
  const allProducts = data?.by_product || [];
  const selected = useMemo(() => {
    if (!selectedProduct) return null;
    return allProducts.find((p) => (p.product_id || '__none__') === selectedProduct) || null;
  }, [allProducts, selectedProduct]);

  const summary = selected
    ? {
        spend_monthly: selected.spend_monthly,
        spend_people_monthly: selected.spend_people_monthly,
        spend_infra_monthly: selected.spend_infra_monthly,
        spend_device_monthly: selected.spend_device_monthly,
        spend_infra_purchase: selected.spend_infra_purchase,
        spend_device_purchase: selected.spend_device_purchase,
        people_count: selected.member_count,
        infra_count: selected.infra_count,
        device_count: selected.device_count,
        product_count: 1,
        products_with_spend: selected.spend_monthly > 0 ? 1 : 0,
      }
    : (data?.summary || {});

  const mix = (selected ? selected.mix : (data?.mix || [])).filter((d) => (d.value || 0) > 0);
  const spendByProduct = (data?.spend_by_product || []).filter((d) => (d.value || 0) > 0);
  const people = selected ? (selected.people || []) : (data?.people || []);
  const infrastructure = selected ? (selected.infrastructure || []) : (data?.infrastructure || []);
  const devices = selected ? (selected.devices || []) : (data?.devices || []);

  const selectCategory = (id) => {
    const qs = selectedProduct ? `?product=${encodeURIComponent(selectedProduct)}` : '';
    if (id === 'all') navigate(`/dashboard/assets/economics/spend-mix${qs}`);
    else navigate(`/dashboard/assets/economics/spend-mix/${id}${qs}`);
  };

  const selectProduct = (pid) => {
    const next = new URLSearchParams(searchParams);
    if (!pid) next.delete('product');
    else next.set('product', pid);
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="economics-spend-mix-page">
      <SuperAdminGate user={user}>
        <div style={{ marginBottom: '12px' }}>
          <Link
            to="/dashboard"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6B7280', textDecoration: 'none' }}
          >
            <CaretLeft size={14} /> Overview
          </Link>
          <h2 style={{ margin: '6px 0 2px', fontSize: '16px', fontWeight: 700 }}>Monthly spend mix</h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
            Spend across <strong>all products</strong> — pick a product to see its people / infrastructure / device breakdown.
          </p>
          <EconomicsSourceNote />
        </div>

        {loading ? <SkeletonBlock rows={8} /> : error ? (
          <EmptyState icon={ChartBar} title="Could not load" description={String(error)} />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
              <StatCard
                label={selected ? 'Product spend / mo' : 'Portfolio spend / mo'}
                value={money(summary.spend_monthly, currency)}
                sub={selected
                  ? selected.product_name
                  : `${summary.products_with_spend || 0} of ${summary.product_count || allProducts.length} products with spend`}
                color="#DC2626"
              />
              <StatCard label="People" value={money(summary.spend_people_monthly, currency)} sub={`${summary.people_count || 0} people`} color="#7C3AED" />
              <StatCard label="Infrastructure" value={money(summary.spend_infra_monthly, currency)} sub={`${summary.infra_count || 0} assets · ${money(summary.spend_infra_purchase, currency)} purchase`} color="#D97706" />
              <StatCard label="Devices" value={money(summary.spend_device_monthly, currency)} sub={`${summary.device_count || 0} devices · ${money(summary.spend_device_purchase, currency)} purchase`} color="#6366F1" />
            </div>

            <Panel
              title="Product"
              actions={(
                <select
                  className="input-field"
                  value={selectedProduct}
                  onChange={(e) => selectProduct(e.target.value)}
                  style={{ padding: '6px 10px', fontSize: '12px', minWidth: '220px' }}
                >
                  <option value="">All products ({allProducts.length})</option>
                  {allProducts.map((p) => (
                    <option key={p.product_id || '__none__'} value={p.product_id || '__none__'}>
                      {p.product_name || p.product_id}
                      {p.spend_monthly ? ` — ${money(p.spend_monthly, currency)}/mo` : ' — no spend yet'}
                    </option>
                  ))}
                </select>
              )}
            >
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#6B7280' }}>
                {selected
                  ? `Showing spend mix for ${selected.product_name}. ${selected.client_count || 0} client(s) under this product.`
                  : 'Showing portfolio totals. Select a product to drill into that product only.'}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
                <CategoryPie
                  data={mix}
                  title={selected ? `${selected.product_name} · category mix` : 'Portfolio · category mix'}
                  subtitle={`Total ${money(summary.spend_monthly, currency)} / mo`}
                  onSliceClick={(key) => {
                    const k = String(key || '').toLowerCase();
                    if (k === 'people' || k === 'infra' || k === 'devices') selectCategory(k);
                  }}
                />
                {!selected ? (
                  <CategoryPie
                    data={spendByProduct}
                    title="Spend by product"
                    subtitle="Click a product slice to open its detail"
                    onSliceClick={(key) => {
                      const k = String(key || '');
                      if (k) selectProduct(k);
                    }}
                  />
                ) : (
                  <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px' }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700 }}>Product snapshot</h3>
                    <div style={{ fontSize: '12px', color: '#374151', lineHeight: 1.7 }}>
                      <div>Clients: <strong>{selected.client_count || 0}</strong></div>
                      <div>People line items: <strong>{selected.member_count || 0}</strong></div>
                      <div>Infrastructure: <strong>{selected.infra_count || 0}</strong></div>
                      <div>Devices: <strong>{selected.device_count || 0}</strong></div>
                      <div style={{ marginTop: '8px' }}>
                        <button type="button" className="btn-secondary" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => selectProduct('')}>
                          Back to all products
                        </button>
                        {selected.product_id ? (
                          <button
                            type="button"
                            className="btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '12px', marginLeft: '6px' }}
                            onClick={() => navigate(`/dashboard/assets/economics/products/${encodeURIComponent(selected.product_id)}`)}
                          >
                            Open product economics
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Panel>

            {!selected && (
              <Panel title={`All products (${allProducts.length})`}>
                {allProducts.length === 0 ? (
                  <EmptyHint>No products found.</EmptyHint>
                ) : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Clients</th>
                          <th>People / mo</th>
                          <th>Infra / mo</th>
                          <th>Devices / mo</th>
                          <th>Total spend / mo</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {allProducts.map((p) => (
                          <tr key={p.product_id || '__none__'}>
                            <td style={{ fontWeight: 600 }}>{p.product_name || p.product_id}</td>
                            <td>{p.client_count || 0}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.spend_people_monthly, currency)}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.spend_infra_monthly, currency)}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(p.spend_device_monthly, currency)}</td>
                            <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>{money(p.spend_monthly, currency)}</td>
                            <td>
                              <button
                                type="button"
                                className="btn-secondary"
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                onClick={() => selectProduct(p.product_id || '__none__')}
                              >
                                Open mix
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={activeCat === c.id ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                  onClick={() => selectCategory(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {(activeCat === 'all' || activeCat === 'people') && (
              <Panel title={`People (${people.length})${selected ? ` · ${selected.product_name}` : ' · all products'}`}>
                {people.length === 0 ? <EmptyHint>No people costs{selected ? ' for this product' : ''} yet. Set member CTC under client Economics or assign members to clients.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Member</th>
                          {!selected ? <th>Product</th> : null}
                          <th>Clients served</th>
                          <th>Monthly CTC</th>
                          <th>Allocated</th>
                          <th>Client split</th>
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p, idx) => (
                          <tr key={`${p.user_id}-${p.product_id || ''}-${idx}`}>
                            <td style={{ fontWeight: 600 }}>{p.name}</td>
                            {!selected ? <td>{p.product_name || '—'}</td> : null}
                            <td>{p.client_count || 0}</td>
                            <td><LineMoney value={p.monthly_cost} currency={currency} /></td>
                            <td><LineMoney value={p.shared_monthly_cost || p.allocated_total} currency={currency} /></td>
                            <td style={{ fontSize: '12px', color: '#6B7280', maxWidth: '320px' }}>
                              {(p.clients || []).length === 0 ? (
                                <span style={{ color: '#9CA3AF' }}>{p.allocation === 'product' ? 'Product team (no client)' : '—'}</span>
                              ) : (p.clients || []).map((c) => (
                                <button
                                  key={c.client_id}
                                  type="button"
                                  onClick={() => navigate(`/dashboard/assets/economics/clients/${c.client_id}`)}
                                  style={{
                                    display: 'inline-block', marginRight: '6px', marginBottom: '4px',
                                    background: '#F3F4F6', border: 'none', borderRadius: '4px',
                                    padding: '2px 6px', fontSize: '11px', cursor: 'pointer',
                                  }}
                                >
                                  {c.client_name} (<LineMoney value={c.shared_monthly_cost} currency={currency} />)
                                </button>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {(activeCat === 'all' || activeCat === 'infra') && (
              <Panel title={`Infrastructure (${infrastructure.length})${selected ? ` · ${selected.product_name}` : ' · all products'}`}>
                {infrastructure.length === 0 ? <EmptyHint>No infrastructure costs{selected ? ' for this product' : ''} yet.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Application / asset</th>
                          {!selected ? <th>Product</th> : null}
                          <th>Client</th>
                          <th>Environment</th>
                          <th>Purchase</th>
                          <th>Monthly</th>
                        </tr>
                      </thead>
                      <tbody>
                        {infrastructure.map((row) => (
                          <tr
                            key={row.asset_id}
                            style={{ cursor: row.client_id ? 'pointer' : 'default' }}
                            onClick={() => row.client_id && navigate(`/dashboard/assets/economics/clients/${row.client_id}`)}
                          >
                            <td style={{ fontWeight: 600 }}>{row.application_name || row.name}</td>
                            {!selected ? <td>{row.product_name || '—'}</td> : null}
                            <td>{row.client_name || '—'}</td>
                            <td>{row.environment || '—'}</td>
                            <td><LineMoney value={row.cost} currency={currency} /></td>
                            <td><LineMoney value={row.monthly_cost} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {(activeCat === 'all' || activeCat === 'devices') && (
              <Panel title={`Devices (${devices.length})${selected ? ` · ${selected.product_name}` : ' · all products'}`}>
                {devices.length === 0 ? <EmptyHint>No device costs{selected ? ' for this product' : ''} yet.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Device</th>
                          <th>Type</th>
                          <th>Assignee</th>
                          {!selected ? <th>Product</th> : null}
                          <th>Client</th>
                          <th>Share</th>
                          <th>Purchase alloc.</th>
                          <th>Monthly alloc.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((row) => (
                          <tr
                            key={`${row.asset_id}-${row.client_id || row.product_id}`}
                            style={{ cursor: row.client_id ? 'pointer' : 'default' }}
                            onClick={() => row.client_id && navigate(`/dashboard/assets/economics/clients/${row.client_id}`)}
                          >
                            <td style={{ fontWeight: 600 }}>{row.name}</td>
                            <td style={{ textTransform: 'capitalize' }}>{fmtLabel(row.asset_type)}</td>
                            <td>{row.assigned_to_name || '—'}</td>
                            {!selected ? <td>{row.product_name || '—'}</td> : null}
                            <td>{row.client_name || '—'}</td>
                            <td>{row.share === 'shared' ? `1/${row.client_count}` : (row.share || '—')}</td>
                            <td><LineMoney value={row.allocated_purchase} currency={currency} /></td>
                            <td><LineMoney value={row.allocated_monthly} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}
          </>
        )}
      </SuperAdminGate>
    </div>
  );
}

export default EconomicsSpendMixPage;
